import { useApiUtils } from '@core/util/apiUtils';
import { Backdrop, CircularProgress } from '@material-ui/core';
import { dreApi } from 'app/services/api/dreApi';
import { categoriaApi, categoriaClasseApi, categoriaVinculoApi } from 'app/services/api/planoContasApi';
import * as Actions from 'app/store/actions';
import { eventReactGA, initializeReactGA } from 'app/utils/analyticsUtil';
import React, { useContext, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { ConfiguracaoContext } from '../configuracao/ConfiguracaoContext';
import { CategoriaContext } from './CategoriaContext';
import { CategoriaDialog } from './components';
import CategoriaTable from './components/CategoriaTable/CategoriaTable';

const CATEGORIA = 'Categoria';

const MAX_NIVEL = 2;

const filtroSintetico = (classe) => classe?.nivel === undefined || classe?.nivel < MAX_NIVEL;

const filtroAnalitico = (classe) => !classe?.classesFilhas || classe?.classesFilhas?.length === 0;

const reduceClasses = (classeList, filtro) => {
  function getAll(array) {
    return array.reduce((r, a) => {
      if (filtro(a)) {
        r.push(a);
      }

      if (a?.classesFilhas) {
        r.push(...getAll(a.classesFilhas, filtro));
      }
      return r;
    }, []);
  }

  return getAll(classeList);
};

const Categoria = () => {
  const [classesReceita, setClassesReceita] = useContext(CategoriaContext).classesReceita;
  const [classesDespesa, setClassesDespesa] = useContext(CategoriaContext).classesDespesa;
  const [, setPlanoContasId] = useContext(CategoriaContext).planoContasId;
  const [, setPlanoContasUtilizado] = useContext(CategoriaContext).planoContasUtilizado;

  const [, setDreReceitaList] = useContext(CategoriaContext).dreReceitaList;
  const [, setDreDespesaList] = useContext(CategoriaContext).dreDespesaList;
  const [, setVinculosAutomaticos] = useContext(CategoriaContext).vinculosAutomaticos;
  const [loading, setLoading] = useContext(CategoriaContext).loading;
  const [readOnly] = useContext(ConfiguracaoContext).categoriaReadOnly;

  const [classesAnaliticoSintetico, setClassesAnaliticoSintetico] = useContext(
    CategoriaContext
  ).classesAnaliticoSintetico;

  const dispatch = useDispatch();
  const apiUtils = useApiUtils(dispatch, Actions);

  const history = useHistory();

  const pathname = history?.location?.pathname;
  useEffect(() => {
    initializeReactGA(`${pathname}/categoria`);
  }, [pathname]);

  const showSuccess = (message) => {
    dispatch(Actions.showMessage({ message, variant: 'success' }));
  };

  const loadCategoriaByEmpresa = async () => {
    setLoading(true);
    try {
      const response = await categoriaApi.getByEmpresa();

      if (response.data.classes) {
        setClassesReceita(response.data.classes.filter((classe) => classe.natureza === 'RECEITA'));
        setClassesDespesa(response.data.classes.filter((classe) => classe.natureza === 'DESPESA'));
      }

      setPlanoContasId(response.data.id);
      setPlanoContasUtilizado(response.data.utilizado);

      const vinculos = {};

      if (response.data.vinculosAutomaticos) {
        response.data.vinculosAutomaticos.forEach((v) => {
          vinculos[`${v.natureza}_${v.processo}`] = v;
        });
      }
      setVinculosAutomaticos(vinculos);
    } catch (error) {
      apiUtils.handleApiError(error);
    }

    setLoading(false);
  };

  const loadDres = async () => {
    try {
      const dres = await dreApi.get();
      setDreReceitaList(dres.filter((dre) => dre.natureza === 'RECEITA'));
      setDreDespesaList(dres.filter((dre) => dre.natureza === 'DESPESA'));
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    loadCategoriaByEmpresa();
    loadDres();
    // Efeito de montagem: incluir as dependências reexecutaria a carga inicial a cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Quando atualizado classesReceita ou classesDespesas (carga inicial da api)
   * atualiza as listagens de sintetico/analitico utilizadas nos modais e
   * vinculo automático
   */
  useEffect(() => {
    setClassesAnaliticoSintetico({
      receita: {
        analitico: reduceClasses(classesReceita, filtroAnalitico),
        sintetico: [{ id: '-', indice: '1.', nome: 'Receita' }, ...reduceClasses(classesReceita, filtroSintetico)],
      },
      despesa: {
        analitico: reduceClasses(classesDespesa, filtroAnalitico),
        sintetico: [{ id: '-', indice: '2.', nome: 'Despesa' }, ...reduceClasses(classesDespesa, filtroSintetico)],
      },
    });
  }, [classesReceita, classesDespesa, setClassesAnaliticoSintetico]);

  const handleEdit = async (classe) => {
    setLoading(true);
    let novaClasse = false;
    try {
      const isReceita = classe?.natureza === 'RECEITA';
      eventReactGA(CATEGORIA, `Atualizar categoria de ${isReceita ? 'receita' : 'despesa'}`);
      novaClasse = await categoriaClasseApi.put(classe);

      showSuccess('Item editado com sucesso');
    } catch (error) {
      apiUtils.handleApiError(error);
    } finally {
      setLoading(false);
    }

    return novaClasse;
  };

  const handleAdd = async (classe, classesReceita, classesDespesa) => {
    let novaClasse = false;
    setLoading(true);

    try {
      const isReceita = classe?.natureza === 'RECEITA';
      eventReactGA(CATEGORIA, `Adicionar categoria de ${isReceita ? 'receita' : 'despesa'}`);
      const res = await categoriaClasseApi.post(classe);

      novaClasse = res.data;

      if (!novaClasse?.classePaiId) {
        if (isReceita) {
          setClassesReceita([...classesReceita, novaClasse]);
        } else {
          setClassesDespesa([...classesDespesa, novaClasse]);
        }
      } else {
        const listaClasses = JSON.parse(JSON.stringify(isReceita ? classesReceita : classesDespesa));

        adicionarClasse(listaClasses, novaClasse);

        if (isReceita) {
          setClassesReceita(listaClasses);
        } else {
          setClassesDespesa(listaClasses);
        }
      }

      showSuccess('Item adicionado com sucesso.');
    } catch (error) {
      apiUtils.handleApiError(error);
    }

    setLoading(false);

    return novaClasse;
  };

  function adicionarClasse(lista, novaClasse) {
    for (const classe of lista) {
      if (classe.id === novaClasse.classePaiId) {
        if (!classe.classesFilhas) {
          classe.classesFilhas = [];
        }
        classe.dreId = undefined; // RNE-976
        classe.classesFilhas.push(novaClasse);
        return true;
      }

      if (classe.classesFilhas?.length && adicionarClasse(classe.classesFilhas, novaClasse)) {
        return true;
      }
    }
    return false;
  }

  const handleChangeVinculoAutomatico = async (vinculosAutomaticos, vinculo, chave) => {
    setLoading(true);

    const isReceita = chave.includes('RECEITA');

    try {
      if (!vinculo.classeId) {
        eventReactGA(CATEGORIA, `Removendo vínculo automático de ${isReceita ? 'receita' : 'despesa'}`);
        await categoriaVinculoApi.desvincular(vinculo.id);
      } else {
        eventReactGA(CATEGORIA, `Atualizando vínculo automático de ${isReceita ? 'receita' : 'despesa'}`);
        delete vinculo.id;
        const res = await categoriaVinculoApi.vincular(vinculo);
        vinculo.id = res.data?.id;
        showSuccess('Vinculado com sucesso');
      }

      setVinculosAutomaticos({
        ...vinculosAutomaticos,
        [`${chave}`]: vinculo,
      });
    } catch (error) {
      apiUtils.handleApiError(error);
    }

    setLoading(false);
  };

  return useMemo(
    () => (
      <>
        <CategoriaTable
          id="categoria"
          onAdd={handleAdd}
          onEdit={handleEdit}
          onChangeVinculoAutomatico={handleChangeVinculoAutomatico}
          classesReceita={classesReceita}
          classesDespesa={classesDespesa}
          showVinculo={classesReceita || classesDespesa}
        />

        <Backdrop style={{ zIndex: 999 }} open={loading}>
          <CircularProgress />
        </Backdrop>

        <CategoriaDialog onAdd={handleAdd} />
      </>
    ),
    // Memoização intencional: as funções são recriadas a cada render e incluí-las anularia o memo;
    // o estado que elas leem já está nas dependências.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [classesReceita, classesDespesa, loading, classesAnaliticoSintetico, readOnly]
  );
};

export default Categoria;
